import { STORAGE_DEVICE_ID, STORAGE_SESSION, STORAGE_TAB_ID } from './constants';

type StoreKind = 'local' | 'session';

function store(kind: StoreKind): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    // Zugriff kann z. B. in Sandbox-iframes oder bei blockierten Cookies werfen.
    return null;
  }
}

export function storageGet(kind: StoreKind, key: string): string | null {
  try {
    return store(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function storageSet(kind: StoreKind, key: string, value: string): void {
  try {
    store(kind)?.setItem(key, value);
  } catch {
    // Quota oder Privatmodus: ohne Persistenz weiterarbeiten.
  }
}

export function storageRemove(kind: StoreKind, key: string): void {
  try {
    store(kind)?.removeItem(key);
  } catch {
    // ignorieren
  }
}

/** Zufällige ID (UUID v4, 36 Zeichen) – passt in die Protokollgrenzen 8..64. */
export function randomId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function isValidId(v: string | null): v is string {
  return v !== null && v.length >= 8 && v.length <= 64;
}

/** `deviceId` pro Browser (localStorage), stabil über Tabs und Reloads. */
export function getDeviceId(): string {
  const existing = storageGet('local', STORAGE_DEVICE_ID);
  if (isValidId(existing)) return existing;
  const id = randomId();
  storageSet('local', STORAGE_DEVICE_ID, id);
  return id;
}

/** `tabId` pro Tab (sessionStorage), überlebt Reloads im selben Tab. */
export function getTabId(): string {
  const existing = storageGet('session', STORAGE_TAB_ID);
  if (isValidId(existing)) return existing;
  const id = randomId();
  storageSet('session', STORAGE_TAB_ID, id);
  return id;
}

/** Für Resume nach Vollseiten-Navigation (Spec Kap. 5 Punkt 8). */
export interface StoredSession {
  sessionId: string;
  resumeToken: string;
  agentName: string;
}

export function loadSession(): StoredSession | null {
  const raw = storageGet('session', STORAGE_SESSION);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<StoredSession> | null;
    if (
      v &&
      typeof v.sessionId === 'string' &&
      typeof v.resumeToken === 'string' &&
      typeof v.agentName === 'string'
    ) {
      return { sessionId: v.sessionId, resumeToken: v.resumeToken, agentName: v.agentName };
    }
  } catch {
    // kaputter Eintrag
  }
  storageRemove('session', STORAGE_SESSION);
  return null;
}

export function saveSession(s: StoredSession): void {
  storageSet('session', STORAGE_SESSION, JSON.stringify(s));
}

export function clearSession(): void {
  storageRemove('session', STORAGE_SESSION);
}
